import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';
import LanguageSelector from './LanguageSelector';
import { TRANSLATION_LANGUAGES, getLanguageByCode } from '../../utils/languages';
import {
  Send, Loader2, Mail, Clock, Star, Inbox, FileText, AlertTriangle,
  Trash2, Archive, Search, Plus, ChevronLeft, Reply, MoreHorizontal,
  Pen, Settings, Folder, BookOpen, Eye, EyeOff, X, RefreshCw,
  Languages, Sparkles, MessageSquare, AlertCircle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Sidebar folder icon map
const FOLDER_ICONS = {
  inbox: Inbox, starred: Star, sent: Send, drafts: FileText,
  important: AlertCircle, archived: Archive, spam: AlertTriangle, trash: Trash2,
};

const EmailToolTab = () => {
  const { language } = useLanguage();
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [conversations, setConversations] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailDetail, setEmailDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [folderCounts, setFolderCounts] = useState({});
  const [customFolders, setCustomFolders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [signature, setSignature] = useState({ name: '', html: '', text: '' });
  const [view, setView] = useState('list'); // list | detail | compose | signature | templates
  const [composeForm, setComposeForm] = useState({ to_email: '', to_name: '', subject: '', body_text: '', body_html: '', cc: '', bcc: '', quoted_html: '' });
  const [showQuoted, setShowQuoted] = useState(false);
  const [replyMode, setReplyMode] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [templateForm, setTemplateForm] = useState({ name: '', subject: '', body_html: '' });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [folderMenuId, setFolderMenuId] = useState(null);
  const [aiLoading, setAiLoading] = useState('');
  // Per-message translation cache: { [messageId]: { lang, text } }
  const [messageTranslations, setMessageTranslations] = useState({});
  const [translatingMessageId, setTranslatingMessageId] = useState(null);
  const [summaryText, setSummaryText] = useState('');
  const [quickReplies, setQuickReplies] = useState([]);
  // Track which message in the thread is being replied to (for In-Reply-To)
  const [activeReplyTarget, setActiveReplyTarget] = useState(null);

  const t = language === 'es' ? {
    compose: 'Redactar', editSignature: 'Editar Firma', search: 'Buscar correo',
    inbox: 'Recibidos', starred: 'Destacados', sent: 'Enviados', drafts: 'Borradores',
    important: 'Importantes', archived: 'Archivados', spam: 'Spam', trash: 'Papelera',
    folders: 'CARPETAS', templates: 'Plantillas', send: 'Enviar', to: 'Para',
    subject: 'Asunto', message: 'Mensaje', cc: 'CC', bcc: 'CCO', back: 'Volver',
    reply: 'Responder', noEmails: 'No hay correos', loading: 'Cargando...',
    name: 'Nombre', save: 'Guardar', cancel: 'Cancelar', delete: 'Eliminar',
    newFolder: 'Nueva carpeta', signatureTitle: 'Firma de Email',
    signaturePlaceholder: 'Escribe tu firma aquí...',
    templateName: 'Nombre de plantilla', useTemplate: 'Usar plantilla',
    createTemplate: 'Crear plantilla', translate: 'Traducir', summarize: 'Resumir',
    generateDraft: 'Borrador IA', quickReplies: 'Respuestas rápidas',
    moveToFolder: 'Mover a carpeta', markRead: 'Marcar leído', markUnread: 'Marcar no leído',
    archive: 'Archivar', markImportant: 'Importante', emptyTrash: 'Vaciar papelera',
    from: 'De', date: 'Fecha', attachments: 'Adjuntos',
  } : {
    compose: 'Compose', editSignature: 'Edit Signature', search: 'Search email',
    inbox: 'Inbox', starred: 'Starred', sent: 'Sent', drafts: 'Drafts',
    important: 'Important', archived: 'Archived', spam: 'Spam', trash: 'Trash',
    folders: 'FOLDERS', templates: 'Templates', send: 'Send', to: 'To',
    subject: 'Subject', message: 'Message', cc: 'CC', bcc: 'BCC', back: 'Back',
    reply: 'Reply', noEmails: 'No emails', loading: 'Loading...',
    name: 'Name', save: 'Save', cancel: 'Cancel', delete: 'Delete',
    newFolder: 'New folder', signatureTitle: 'Email Signature',
    signaturePlaceholder: 'Write your signature here...',
    templateName: 'Template name', useTemplate: 'Use template',
    createTemplate: 'Create template', translate: 'Translate', summarize: 'Summarize',
    generateDraft: 'AI Draft', quickReplies: 'Quick replies',
    moveToFolder: 'Move to folder', markRead: 'Mark read', markUnread: 'Mark unread',
    archive: 'Archive', markImportant: 'Important', emptyTrash: 'Empty trash',
    from: 'From', date: 'Date', attachments: 'Attachments',
  };

  const SYSTEM_FOLDERS = ['inbox', 'starred', 'sent', 'drafts', 'important', 'archived', 'spam', 'trash'];

  // Load conversations
  const loadConversations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email-tool/conversations`, {
        params: { folder: activeFolder, search: searchQuery, limit: 50 }
      });
      setConversations(res.data.conversations || []);
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeFolder, searchQuery]);

  const loadFolderCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email-tool/folder-counts`);
      setFolderCounts(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const loadCustomFolders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email-tool/custom-folders`);
      setCustomFolders(res.data.folders || []);
    } catch (err) { console.error(err); }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email-tool/templates`);
      setTemplates(res.data.templates || []);
    } catch (err) { console.error(err); }
  }, []);

  const loadSignature = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email-tool/signature`);
      setSignature(res.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);
  useEffect(() => { loadFolderCounts(); loadCustomFolders(); loadTemplates(); loadSignature(); }, []);

  // Open email
  const openEmail = async (conv) => {
    setSelectedEmail(conv);
    setView('detail');
    setMessageTranslations({});
    setSummaryText('');
    setQuickReplies([]);
    try {
      const res = await axios.get(`${API}/email-tool/conversations/${conv.thread_id}`);
      setEmailDetail(res.data);
      loadFolderCounts();
    } catch (err) {
      setEmailDetail({ email: conv, messages: [], replies: [] });
    }
  };

  // Send email
  const handleSend = async () => {
    if (!composeForm.to_email || !composeForm.subject) {
      toast.error(language === 'es' ? 'Rellena destinatario y asunto' : 'Fill recipient and subject');
      return;
    }
    setSending(true);
    try {
      // Concatenate user-typed body + quoted block (Gmail-style)
      const finalHtml = composeForm.quoted_html
        ? `${composeForm.body_html || ''}${composeForm.quoted_html}`
        : (composeForm.body_html || null);
      const finalText = (composeForm.body_html || '').replace(/<[^>]+>/g, ' ').trim();

      const payload = {
        ...composeForm,
        body_text: finalText || composeForm.body_text,
        body_html: finalHtml,
        cc: composeForm.cc || null,
        bcc: composeForm.bcc || null,
      };
      delete payload.quoted_html;
      if (replyMode && selectedEmail) {
        payload.thread_id = selectedEmail.thread_id;
        if (activeReplyTarget?.message_id) {
          payload.in_reply_to = activeReplyTarget.message_id;
          payload.references = activeReplyTarget.references || activeReplyTarget.message_id;
        }
        await axios.post(`${API}/email-tool/conversations/reply`, payload);
      } else {
        await axios.post(`${API}/email-tool/send`, payload);
      }
      toast.success(language === 'es' ? 'Email enviado' : 'Email sent');
      setComposeForm({ to_email: '', to_name: '', subject: '', body_text: '', body_html: '', cc: '', bcc: '', quoted_html: '' });
      setShowQuoted(false);
      setReplyMode(false);
      setActiveReplyTarget(null);
      setView('list');
      loadConversations();
      loadFolderCounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSending(false);
    }
  };

  // Actions
  const handleStar = async (threadId, starred) => {
    await axios.put(`${API}/email-tool/conversations/${threadId}/star`, { starred: !starred });
    loadConversations();
    loadFolderCounts();
  };

  const handleDelete = async (threadId) => {
    if (!confirm(language === 'es' ? '¿Mover a papelera?' : 'Move to trash?')) return;
    await axios.delete(`${API}/email-tool/conversations/${threadId}`);
    toast.success(language === 'es' ? 'Movido a papelera' : 'Moved to trash');
    setView('list');
    setSelectedEmail(null);
    loadConversations();
    loadFolderCounts();
  };

  const handleArchive = async (threadId) => {
    await axios.post(`${API}/email-tool/archive`, { email_id: threadId });
    toast.success(language === 'es' ? 'Archivado' : 'Archived');
    setView('list');
    setSelectedEmail(null);
    loadConversations();
    loadFolderCounts();
  };

  const handleMarkImportant = async (threadId) => {
    await axios.post(`${API}/email-tool/mark-important`, { email_id: threadId, important: true });
    toast.success(language === 'es' ? 'Marcado como importante' : 'Marked important');
    loadFolderCounts();
  };

  const handleMarkUnread = async (threadId) => {
    await axios.put(`${API}/email-tool/conversations/${threadId}/unread`);
    toast.success(language === 'es' ? 'Marcado como no leído' : 'Marked unread');
    setView('list');
    setSelectedEmail(null);
    loadConversations();
    loadFolderCounts();
  };

  const handleEmptyTrash = async () => {
    if (!confirm(language === 'es' ? '¿Vaciar papelera? Esta acción no se puede deshacer.' : 'Empty trash? This cannot be undone.')) return;
    await axios.delete(`${API}/email-tool/conversations/trash/empty`);
    toast.success(language === 'es' ? 'Papelera vaciada' : 'Trash emptied');
    loadConversations();
    loadFolderCounts();
  };

  const handleMoveToFolder = async (threadId, folder) => {
    await axios.put(`${API}/email-tool/conversations/${threadId}/folder`, { folder });
    const folderName = customFolders.find(f => f.folder_id === folder)?.name || folder;
    toast.success(language === 'es' ? `Movido a ${folderName}` : `Moved to ${folderName}`);
    loadConversations();
    loadFolderCounts();
  };

  const handleSync = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/email-tool/sync`);
      await loadConversations();
      await loadFolderCounts();
      toast.success(language === 'es' ? 'Sincronizado' : 'Synced');
    } catch (err) { toast.error('Error'); }
    finally { setLoading(false); }
  };

  // Save signature
  const handleSaveSignature = async () => {
    try {
      await axios.put(`${API}/email-tool/signature`, signature);
      toast.success(language === 'es' ? 'Firma guardada' : 'Signature saved');
      setView('list');
    } catch (err) { toast.error('Error'); }
  };

  // Create custom folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await axios.post(`${API}/email-tool/custom-folders`, { name: newFolderName });
      setNewFolderName('');
      setShowNewFolder(false);
      loadCustomFolders();
    } catch (err) { toast.error('Error'); }
  };

  // Templates
  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject) return;
    try {
      if (editingTemplate) {
        await axios.put(`${API}/email-tool/templates/${editingTemplate}`, templateForm);
      } else {
        await axios.post(`${API}/email-tool/templates`, templateForm);
      }
      toast.success(language === 'es' ? 'Plantilla guardada' : 'Template saved');
      setTemplateForm({ name: '', subject: '', body_html: '' });
      setEditingTemplate(null);
      loadTemplates();
    } catch (err) { toast.error('Error'); }
  };

  const handleUseTemplate = (tmpl) => {
    setComposeForm(prev => ({
      ...prev,
      subject: tmpl.subject,
      body_html: tmpl.body_html,
      body_text: tmpl.body_text || '',
    }));
    setShowTemplates(false);
    setView('compose');
  };

  // Per-message translate (used in Gmail-style thread)
  const handleTranslateMessage = async (msg, lang) => {
    const text = msg.body_text || (msg.body_html || '').replace(/<[^>]+>/g, ' ').trim();
    if (!text) {
      toast.error(language === 'es' ? 'Sin contenido' : 'No content');
      return;
    }
    setTranslatingMessageId(msg.id);
    try {
      const res = await axios.post(`${API}/email-tool/translate`, { text, target_language: lang });
      setMessageTranslations(prev => ({ ...prev, [msg.id]: { lang, text: res.data.translated } }));
      toast.success(language === 'es' ? 'Traducido' : 'Translated');
    } catch { toast.error('Error IA'); }
    finally { setTranslatingMessageId(null); }
  };

  const handleSummarize = async () => {
    if (!emailDetail) return;
    setAiLoading('summarize');
    try {
      const messages = emailDetail.messages || [];
      const texts = messages.length > 0
        ? messages.map(m => `[${m.type === 'sent' ? 'Enviado' : 'Recibido'} - ${m.from_name || m.from_email}]\n${m.body_text || (m.body_html || '').replace(/<[^>]+>/g, ' ')}`)
        : [emailDetail.email?.body_text || emailDetail.email?.preview || ''];
      const res = await axios.post(`${API}/email-tool/summarize`, { texts });
      setSummaryText(res.data.summary);
    } catch { toast.error('Error IA'); }
    finally { setAiLoading(''); }
  };

  const handleQuickReplies = async () => {
    if (!emailDetail) return;
    // Use the latest received message for context if available
    const messages = emailDetail.messages || [];
    const lastReceived = [...messages].reverse().find(m => m.type === 'received') || emailDetail.email;
    setAiLoading('quick');
    try {
      const res = await axios.post(`${API}/email-tool/quick-replies`, {
        email_subject: lastReceived?.subject || '',
        email_body: lastReceived?.body_text || (lastReceived?.body_html || '').replace(/<[^>]+>/g, ' ').trim() || '',
        language: language,
      });
      setQuickReplies(res.data.replies || []);
    } catch { toast.error('Error IA'); }
    finally { setAiLoading(''); }
  };

  const handleGenerateDraft = async () => {
    if (!emailDetail) return;
    const messages = emailDetail.messages || [];
    const lastReceived = [...messages].reverse().find(m => m.type === 'received') || emailDetail.email;
    setAiLoading('draft');
    try {
      const context = `Subject: ${lastReceived?.subject}\nFrom: ${lastReceived?.from_name}\nBody: ${lastReceived?.body_text || (lastReceived?.body_html || '').replace(/<[^>]+>/g, ' ').trim() || ''}`;
      const res = await axios.post(`${API}/email-tool/generate-draft`, {
        context, tone: 'professional', language: language,
      });
      setComposeForm(p => ({ ...p, body_html: res.data.draft.replace(/\n/g, '<br>'), body_text: res.data.draft }));
      toast.success(language === 'es' ? 'Borrador generado' : 'Draft generated');
    } catch { toast.error('Error IA'); }
    finally { setAiLoading(''); }
  };

  const handleTranslateCompose = async (lang) => {
    if (!composeForm.body_html && !composeForm.body_text) return;
    setAiLoading('translate-compose');
    try {
      const text = composeForm.body_text || composeForm.body_html.replace(/<[^>]+>/g, ' ').trim();
      const res = await axios.post(`${API}/email-tool/translate`, { text, target_language: lang });
      setComposeForm(p => ({ ...p, body_html: res.data.translated.replace(/\n/g, '<br>'), body_text: res.data.translated }));
      toast.success(language === 'es' ? 'Traducido' : 'Translated');
    } catch { toast.error('Error IA'); }
    finally { setAiLoading(''); }
  };

  const handleUseQuickReply = (reply) => {
    const messages = emailDetail?.messages || [];
    const lastReceived = [...messages].reverse().find(m => m.type === 'received') || emailDetail?.email;
    setActiveReplyTarget(lastReceived);
    const date = lastReceived?.date ? new Date(lastReceived.date).toLocaleString() : '';
    const fromLabel = lastReceived?.from_name || lastReceived?.from_email || '';
    const quotedText = lastReceived?.body_text || '';
    const quotedHtml = `<br><br><div style="border-left:2px solid #ccc;padding-left:12px;margin-left:4px;color:#555;"><p style="font-size:12px;color:#888;">El ${date}, ${fromLabel} escribió:</p>${lastReceived?.body_html || quotedText.replace(/\n/g, '<br>')}</div>`;
    setComposeForm({
      to_email: lastReceived?.from_email || '',
      to_name: lastReceived?.from_name || '',
      subject: lastReceived?.subject?.startsWith('Re:') ? lastReceived.subject : `Re: ${lastReceived?.subject || ''}`,
      body_text: reply, body_html: reply.replace(/\n/g, '<br>'), cc: '', bcc: '',
      quoted_html: quotedHtml,
    });
    setShowQuoted(false);
    setReplyMode(true);
    setQuickReplies([]);
    setView('compose');
  };

  // Reply to a specific message in the thread (target = message obj). Falls back to last received.
  const handleReply = (target = null) => {
    if (!emailDetail) return;
    const messages = emailDetail.messages || [];
    const msg = target || [...messages].reverse().find(m => m.type === 'received') || emailDetail.email;
    if (!msg) return;
    setActiveReplyTarget(msg);
    const date = msg.date ? new Date(msg.date).toLocaleString() : '';
    const fromLabel = msg.from_name || msg.from_email || (msg.type === 'sent' ? 'KeyPubli' : '');
    const quotedText = msg.body_text || '';
    // Quoted block is stored separately and rendered below the editor (Gmail-style)
    const quotedHtml = `<br><br><div style="border-left:2px solid #ccc;padding-left:12px;margin-left:4px;color:#555;"><p style="font-size:12px;color:#888;">El ${date}, ${fromLabel} escribió:</p>${msg.body_html || quotedText.replace(/\n/g, '<br>')}</div>`;
    const toEmail = msg.type === 'received' ? (msg.from_email || '') : (msg.to_email || '');
    const toName = msg.type === 'received' ? (msg.from_name || '') : (msg.to_name || '');
    setComposeForm({
      to_email: toEmail,
      to_name: toName,
      subject: msg.subject?.startsWith('Re:') ? msg.subject : `Re: ${msg.subject || ''}`,
      body_text: '',
      body_html: '',  // Empty editor — user types in clean field
      cc: '', bcc: '',
      quoted_html: quotedHtml,  // Quoted text rendered separately below editor
    });
    setShowQuoted(false);
    setReplyMode(true);
    setView('compose');
  };

  // Get initial + color for avatar
  const getAvatar = (name, email) => {
    const initial = (name || email || '?')[0].toUpperCase();
    const colors = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];
    const idx = (initial.charCodeAt(0) * 7) % colors.length;
    return { initial, color: colors[idx] };
  };

  // ==================== RENDER ====================

  return (
    <div className="flex h-[calc(100vh-220px)] min-h-[500px] bg-white rounded-lg border overflow-hidden" data-testid="email-tool">
      {/* Sidebar */}
      <div className="w-56 border-r bg-amber-50/50 flex flex-col shrink-0">
        <div className="p-3">
          <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white" size="sm"
            onClick={() => { setComposeForm({ to_email: '', to_name: '', subject: '', body_text: '', body_html: '', cc: '', bcc: '', quoted_html: '' }); setShowQuoted(false); setActiveReplyTarget(null); setReplyMode(false); setView('compose'); }}
            data-testid="compose-btn">
            <Pen className="h-4 w-4 mr-2" />{t.compose}
          </Button>
        </div>
        <button className="flex items-center gap-2 px-4 py-1.5 text-sm text-slate-600 hover:bg-amber-100/50 text-left"
          onClick={() => { loadSignature(); setView('signature'); }}>
          <Settings className="h-3.5 w-3.5" />{t.editSignature}
        </button>

        <div className="flex-1 overflow-y-auto mt-2">
          {SYSTEM_FOLDERS.map((fid) => {
            const Icon = FOLDER_ICONS[fid] || Folder;
            const count = folderCounts[fid] || 0;
            const label = t[fid] || fid;
            return (
              <button key={fid}
                className={`flex items-center gap-2 w-full px-4 py-2 text-sm text-left transition-colors ${activeFolder === fid && view === 'list' ? 'bg-amber-200/60 font-medium text-amber-900' : 'text-slate-700 hover:bg-amber-100/40'}`}
                onClick={() => { setActiveFolder(fid); setView('list'); setSelectedEmail(null); }}
                data-testid={`folder-${fid}`}>
                <Icon className="h-4 w-4 shrink-0" />
                <span className="flex-1 truncate">{label}</span>
                {count > 0 && <Badge variant="secondary" className="text-xs px-1.5 py-0 h-5 bg-amber-200 text-amber-800">{count}</Badge>}
              </button>
            );
          })}

          {/* Custom folders */}
          <div className="mt-4 px-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{t.folders}</span>
              <button onClick={() => setShowNewFolder(!showNewFolder)} className="text-slate-400 hover:text-slate-600">
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
            {showNewFolder && (
              <div className="flex gap-1 mb-2">
                <Input className="h-7 text-xs" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder={t.newFolder} onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()} />
                <Button size="sm" className="h-7 px-2" onClick={handleCreateFolder}><Plus className="h-3 w-3" /></Button>
              </div>
            )}
            {customFolders.map((cf) => (
              <button key={cf.folder_id}
                className={`flex items-center gap-2 w-full px-2 py-1.5 text-sm text-left rounded ${activeFolder === cf.folder_id ? 'bg-amber-200/60 font-medium' : 'hover:bg-amber-100/40'}`}
                onClick={() => { setActiveFolder(cf.folder_id); setView('list'); }}>
                <Folder className="h-3.5 w-3.5" style={{ color: cf.color }} />
                <span className="truncate">{cf.name}</span>
                {(folderCounts.custom?.[cf.folder_id] || 0) > 0 && (
                  <Badge variant="secondary" className="text-xs px-1 py-0 h-4">{folderCounts.custom[cf.folder_id]}</Badge>
                )}
              </button>
            ))}
          </div>

          {/* Templates */}
          <button className="flex items-center gap-2 w-full px-4 py-2 text-sm text-left text-slate-700 hover:bg-amber-100/40 mt-2"
            onClick={() => { setView('templates'); loadTemplates(); }}>
            <BookOpen className="h-4 w-4" />
            <span className="flex-1">{t.templates}</span>
            {templates.length > 0 && <Badge variant="secondary" className="text-xs px-1 py-0 h-4">{templates.length}</Badge>}
          </button>
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* === LIST VIEW === */}
        {view === 'list' && (
          <>
            <div className="flex items-center gap-2 p-3 border-b">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input className="pl-9 h-9" placeholder={t.search} value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadConversations()}
                  data-testid="email-search" />
              </div>
              {activeFolder === 'trash' && (
                <Button variant="ghost" size="sm" className="text-red-500 text-xs" onClick={handleEmptyTrash}>
                  <Trash2 className="h-3.5 w-3.5 mr-1" />{t.emptyTrash}
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={handleSync} disabled={loading}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-32 text-sm text-slate-500">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />{t.loading}
                </div>
              ) : conversations.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-sm text-slate-400">{t.noEmails}</div>
              ) : (
                conversations.map((conv) => {
                  const { initial, color } = getAvatar(conv.from_name, conv.from_email);
                  return (
                    <div key={conv.thread_id}
                      className={`group flex items-center gap-3 px-4 py-3 border-b cursor-pointer transition-colors hover:bg-slate-50 ${!conv.read ? 'bg-blue-50/30 font-medium' : ''} ${selectedEmail?.thread_id === conv.thread_id ? 'bg-amber-50' : ''}`}
                      onClick={() => openEmail(conv)}
                      data-testid={`email-item-${conv.thread_id}`}>
                      <button onClick={(e) => { e.stopPropagation(); handleStar(conv.thread_id, conv.starred); }}
                        className="shrink-0">
                        <Star className={`h-4 w-4 ${conv.starred ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`} />
                      </button>
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                        style={{ backgroundColor: color }}>{initial}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm truncate ${!conv.read ? 'font-semibold' : ''}`}>
                            {activeFolder === 'sent' ? (conv.to_email || conv.to_name) : conv.from_name}
                          </span>
                          {conv.has_attachments && <span className="text-xs text-slate-400">📎</span>}
                        </div>
                        <p className="text-sm text-slate-800 truncate">{conv.subject}</p>
                        <p className="text-xs text-slate-500 truncate">{conv.preview}</p>
                      </div>
                      <div className="text-xs text-slate-400 shrink-0 text-right flex flex-col items-end gap-1">
                        <span>{conv.date ? new Date(conv.date).toLocaleDateString() : ''}</span>
                        <div className="hidden group-hover:flex items-center gap-0.5">
                          <button onClick={(e) => { e.stopPropagation(); handleArchive(conv.thread_id); }}
                            className="p-1 rounded hover:bg-slate-200" title={t.archive}><Archive className="h-3.5 w-3.5 text-slate-500" /></button>
                          <button onClick={(e) => { e.stopPropagation(); handleDelete(conv.thread_id); }}
                            className="p-1 rounded hover:bg-red-100" title={t.delete}><Trash2 className="h-3.5 w-3.5 text-red-400" /></button>
                          {conv.read ? (
                            <button onClick={(e) => { e.stopPropagation(); handleMarkUnread(conv.thread_id); }}
                              className="p-1 rounded hover:bg-slate-200" title={t.markUnread}><EyeOff className="h-3.5 w-3.5 text-slate-500" /></button>
                          ) : (
                            <button onClick={(e) => { e.stopPropagation(); axios.put(`${API}/email-tool/conversations/${conv.thread_id}/read`).then(() => { loadConversations(); loadFolderCounts(); }); }}
                              className="p-1 rounded hover:bg-slate-200" title={t.markRead}><Eye className="h-3.5 w-3.5 text-slate-500" /></button>
                          )}
                          {customFolders.length > 0 && (
                            <div className="relative" onClick={(e) => e.stopPropagation()}>
                              <button className="p-1 rounded hover:bg-slate-200" title={t.moveToFolder}
                                onClick={(e) => { e.stopPropagation(); setFolderMenuId(folderMenuId === conv.thread_id ? null : conv.thread_id); }}>
                                <Folder className="h-3.5 w-3.5 text-slate-500" />
                              </button>
                              {folderMenuId === conv.thread_id && (
                                <div className="absolute right-0 top-full mt-1 bg-white border rounded-md shadow-lg z-20 min-w-[140px] py-1">
                                  {customFolders.map(cf => (
                                    <button key={cf.folder_id} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100"
                                      onClick={() => { handleMoveToFolder(conv.thread_id, cf.folder_id); setFolderMenuId(null); }}>
                                      <Folder className="h-3 w-3 inline mr-1.5" style={{ color: cf.color }} />{cf.name}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        {/* === DETAIL VIEW (Gmail-style threading) === */}
        {view === 'detail' && emailDetail && (
          <>
            <div className="flex items-center gap-2 p-3 border-b">
              <Button variant="ghost" size="sm" onClick={() => { setView('list'); setSelectedEmail(null); setMessageTranslations({}); setSummaryText(''); setQuickReplies([]); }}>
                <ChevronLeft className="h-4 w-4 mr-1" />{t.back}
              </Button>
              <h2 className="text-base font-semibold flex-1 truncate ml-2">
                {emailDetail.email?.subject}
                {emailDetail.messages?.length > 1 && (
                  <span className="ml-2 text-xs font-normal text-slate-500">({emailDetail.messages.length})</span>
                )}
              </h2>
              {/* Thread-level AI Buttons */}
              <div className="flex items-center gap-1 mr-2 border-r pr-2">
                <Button variant="ghost" size="sm" disabled={aiLoading === 'summarize'} onClick={handleSummarize} title={t.summarize}>
                  {aiLoading === 'summarize' ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
                </Button>
                <Button variant="ghost" size="sm" disabled={aiLoading === 'quick'} onClick={handleQuickReplies} title={t.quickReplies}>
                  {aiLoading === 'quick' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                </Button>
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleReply()} data-testid="reply-button"><Reply className="h-4 w-4 mr-1" />{t.reply}</Button>
              <Button variant="ghost" size="sm" onClick={() => handleMarkUnread(selectedEmail.thread_id)} title={t.markUnread}><EyeOff className="h-4 w-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => handleArchive(selectedEmail.thread_id)} title={t.archive}><Archive className="h-4 w-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => handleMarkImportant(selectedEmail.thread_id)}><AlertCircle className="h-4 w-4" /></Button>
              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(selectedEmail.thread_id)}><Trash2 className="h-4 w-4" /></Button>
              {customFolders.length > 0 && (
                <div className="relative group">
                  <Button variant="ghost" size="sm"><Folder className="h-4 w-4" /></Button>
                  <div className="absolute right-0 top-full mt-1 bg-white border rounded-md shadow-lg hidden group-hover:block z-10 min-w-[140px]">
                    {customFolders.map(cf => (
                      <button key={cf.folder_id} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100"
                        onClick={() => handleMoveToFolder(selectedEmail.thread_id, cf.folder_id)}>
                        {cf.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto bg-slate-50 p-4 space-y-3" data-testid="thread-messages-container">
              {/* Render each message Gmail-style */}
              {(emailDetail.messages && emailDetail.messages.length > 0
                ? emailDetail.messages
                : [{
                    type: 'received',
                    id: emailDetail.email?.id || selectedEmail?.thread_id,
                    from_name: emailDetail.email?.from_name,
                    from_email: emailDetail.email?.from_email,
                    to: emailDetail.email?.to,
                    subject: emailDetail.email?.subject,
                    body_text: emailDetail.email?.body_text,
                    body_html: emailDetail.email?.body_html,
                    date: emailDetail.email?.date,
                    attachments: emailDetail.email?.attachments,
                  }]
              ).map((msg, idx) => {
                const isSent = msg.type === 'sent';
                const { initial, color } = getAvatar(msg.from_name, msg.from_email);
                const translation = messageTranslations[msg.id];
                const translatedLang = translation?.lang ? getLanguageByCode(translation.lang) : null;
                const dateStr = msg.date ? new Date(msg.date).toLocaleString() : '';

                return (
                  <Card
                    key={`${msg.type}-${msg.id || idx}`}
                    className={`overflow-hidden ${isSent ? 'border-l-4 border-l-teal-500' : 'border-l-4 border-l-blue-400'}`}
                    data-testid={`thread-message-${idx}`}
                  >
                    <div className="flex items-start gap-3 p-4 bg-white">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold shrink-0 text-sm" style={{ backgroundColor: isSent ? '#0d9488' : color }}>
                        {isSent ? 'KP' : initial}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">
                              {isSent ? 'KeyPubli' : (msg.from_name || msg.from_email)}
                              <span className="text-xs text-slate-500 font-normal ml-1.5">
                                &lt;{isSent ? msg.from_email : msg.from_email}&gt;
                              </span>
                            </p>
                            <p className="text-xs text-slate-500">
                              {isSent ? `${t.to}: ${msg.to_email}` : (msg.to ? `${t.to}: ${msg.to}` : '')}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-xs text-slate-500">{dateStr}</span>
                            <LanguageSelector
                              loading={translatingMessageId === msg.id}
                              onSelect={(code) => handleTranslateMessage(msg, code)}
                              compact={true}
                              label={t.translate}
                              value={translation?.lang}
                            />
                            {!isSent && (
                              <Button variant="ghost" size="sm" className="h-7" onClick={() => handleReply(msg)} title={t.reply} data-testid={`reply-msg-${idx}`}>
                                <Reply className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Attachments */}
                        {msg.attachments?.length > 0 && (
                          <div className="mb-3 flex flex-wrap gap-2">
                            {msg.attachments.map((att, i) => (
                              <a key={i} href={`${API}/email-tool/inbox/${msg.id}/attachment/${att.index}`}
                                className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 rounded-md text-xs hover:bg-slate-200"
                                target="_blank" rel="noreferrer">
                                📎 {att.filename}
                                <span className="text-slate-500">({Math.round(att.size/1024)}KB)</span>
                              </a>
                            ))}
                          </div>
                        )}

                        {/* Body */}
                        {msg.body_html ? (
                          <div className="prose prose-sm max-w-none text-sm" dangerouslySetInnerHTML={{ __html: msg.body_html }} />
                        ) : (
                          <div className="whitespace-pre-wrap text-sm text-slate-700">{msg.body_text}</div>
                        )}

                        {/* Per-message translation */}
                        {translation?.text && (
                          <div className="mt-3 border rounded-md bg-blue-50 p-3">
                            <div className="flex items-center justify-between mb-1.5">
                              <h5 className="text-xs font-semibold text-blue-800 flex items-center gap-1.5">
                                <Languages className="h-3.5 w-3.5" />
                                {t.translate} {translatedLang?.flag} {translatedLang?.native}
                              </h5>
                              <button onClick={() => setMessageTranslations(prev => { const n = { ...prev }; delete n[msg.id]; return n; })}
                                className="p-0.5 hover:bg-blue-100 rounded">
                                <X className="h-3 w-3 text-blue-700" />
                              </button>
                            </div>
                            <div className="text-sm whitespace-pre-wrap text-slate-800">{translation.text}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}

              {/* AI: Summary result (thread-level) */}
              {summaryText && (
                <div className="border rounded-md bg-purple-50 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-purple-800 flex items-center gap-1.5">
                      <MessageSquare className="h-4 w-4" />{t.summarize}
                    </h4>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setSummaryText('')}><X className="h-3.5 w-3.5" /></Button>
                  </div>
                  <div className="text-sm whitespace-pre-wrap text-slate-800">{summaryText}</div>
                </div>
              )}

              {/* AI: Quick replies (thread-level) */}
              {quickReplies.length > 0 && (
                <div className="border rounded-md bg-amber-50 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-amber-800 flex items-center gap-1.5">
                      <Sparkles className="h-4 w-4" />{t.quickReplies}
                    </h4>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setQuickReplies([])}><X className="h-3.5 w-3.5" /></Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {quickReplies.map((reply, i) => (
                      <button key={i} className="text-left text-sm p-3 bg-white border rounded-md hover:bg-amber-100 hover:border-amber-300 transition-colors"
                        onClick={() => handleUseQuickReply(reply)}>
                        {reply}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Inline quick reply trigger at bottom */}
              <div className="flex items-center gap-2 pt-2">
                <Button variant="outline" className="text-sm" onClick={() => handleReply()} data-testid="bottom-reply-btn">
                  <Reply className="h-4 w-4 mr-1.5" />{t.reply}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* === COMPOSE VIEW === */}
        {view === 'compose' && (
          <>
            <div className="flex items-center gap-2 p-3 border-b">
              <Button variant="ghost" size="sm" onClick={() => { setView('list'); setReplyMode(false); }}>
                <ChevronLeft className="h-4 w-4 mr-1" />{t.back}
              </Button>
              <h3 className="font-medium flex-1">{replyMode ? t.reply : t.compose}</h3>
              {/* AI in compose toolbar */}
              {replyMode && (
                <Button variant="outline" size="sm" className="text-xs" disabled={aiLoading === 'draft'}
                  onClick={handleGenerateDraft} data-testid="ai-draft-btn">
                  {aiLoading === 'draft' ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                  {t.generateDraft}
                </Button>
              )}
              <div className="flex items-center gap-1 border-l pl-2">
                <LanguageSelector
                  loading={aiLoading === 'translate-compose'}
                  onSelect={(code) => handleTranslateCompose(code)}
                  label={t.translate}
                />
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowTemplates(!showTemplates)}>
                <BookOpen className="h-4 w-4 mr-1" />{t.templates}
              </Button>
              <Button variant="ghost" size="sm" className="text-red-500"
                onClick={() => {
                  if (confirm(language === 'es' ? '¿Descartar este mensaje?' : 'Discard this message?')) {
                    setComposeForm({ to_email: '', to_name: '', subject: '', body_text: '', body_html: '', cc: '', bcc: '', quoted_html: '' });
                    setShowQuoted(false);
                    setActiveReplyTarget(null);
                    setReplyMode(false);
                    setView('list');
                  }
                }}
                title={language === 'es' ? 'Descartar mensaje' : 'Discard message'}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            {showTemplates && templates.length > 0 && (
              <div className="border-b p-2 flex gap-2 flex-wrap bg-slate-50">
                {templates.map(tmpl => (
                  <Button key={tmpl.template_id} variant="outline" size="sm" className="text-xs"
                    onClick={() => handleUseTemplate(tmpl)}>{tmpl.name}</Button>
                ))}
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500">{t.to} *</label>
                  <Input value={composeForm.to_email} onChange={(e) => setComposeForm(p => ({ ...p, to_email: e.target.value }))}
                    placeholder="email@ejemplo.com" data-testid="compose-to" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">{t.name}</label>
                  <Input value={composeForm.to_name} onChange={(e) => setComposeForm(p => ({ ...p, to_name: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">{t.subject} *</label>
                <Input value={composeForm.subject} onChange={(e) => setComposeForm(p => ({ ...p, subject: e.target.value }))}
                  data-testid="compose-subject" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500">{t.cc}</label>
                  <Input value={composeForm.cc} onChange={(e) => setComposeForm(p => ({ ...p, cc: e.target.value }))} placeholder="email1, email2" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">{t.bcc}</label>
                  <Input value={composeForm.bcc} onChange={(e) => setComposeForm(p => ({ ...p, bcc: e.target.value }))} placeholder="email" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">{t.message}</label>
                <RichTextEditor
                  value={composeForm.body_html}
                  onChange={(html) => setComposeForm(p => ({ ...p, body_html: html }))}
                  placeholder={language === 'es' ? 'Escribe tu mensaje...' : 'Write your message...'}
                  minHeight="150px"
                />
                {/* Gmail-style collapsed quoted reply */}
                {composeForm.quoted_html && (
                  <div className="mt-2" data-testid="quoted-block-wrapper">
                    {!showQuoted ? (
                      <button
                        type="button"
                        onClick={() => setShowQuoted(true)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 rounded border border-slate-200 bg-white"
                        title={language === 'es' ? 'Mostrar texto citado' : 'Show quoted text'}
                        data-testid="show-quoted-btn"
                      >
                        <MoreHorizontal className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <div className="border rounded-md bg-slate-50 p-3 relative">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-slate-500">
                            {language === 'es' ? 'Texto citado del mensaje original' : 'Quoted text from original message'}
                          </span>
                          <div className="flex items-center gap-1">
                            <button type="button"
                              onClick={() => setShowQuoted(false)}
                              className="text-xs text-slate-500 hover:text-slate-700 px-2 py-0.5 hover:bg-slate-200 rounded"
                              data-testid="hide-quoted-btn">
                              {language === 'es' ? 'Ocultar' : 'Hide'}
                            </button>
                            <button type="button"
                              onClick={() => { if (confirm(language === 'es' ? '¿Eliminar texto citado del mensaje?' : 'Remove quoted text?')) { setComposeForm(p => ({ ...p, quoted_html: '' })); setShowQuoted(false); } }}
                              className="text-xs text-red-500 hover:text-red-700 px-2 py-0.5 hover:bg-red-100 rounded"
                              data-testid="remove-quoted-btn">
                              {language === 'es' ? 'Eliminar' : 'Remove'}
                            </button>
                          </div>
                        </div>
                        <div className="prose prose-sm max-w-none text-xs text-slate-600 max-h-48 overflow-y-auto"
                          dangerouslySetInnerHTML={{ __html: composeForm.quoted_html }} />
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex justify-end pt-2">
                <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleSend} disabled={sending} data-testid="compose-send">
                  {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  {t.send}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* === SIGNATURE VIEW === */}
        {view === 'signature' && (
          <>
            <div className="flex items-center gap-2 p-3 border-b">
              <Button variant="ghost" size="sm" onClick={() => setView('list')}>
                <ChevronLeft className="h-4 w-4 mr-1" />{t.back}
              </Button>
              <h3 className="font-medium">{t.signatureTitle}</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-500">{t.name}</label>
                <Input value={signature.name} onChange={(e) => setSignature(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">HTML</label>
                <RichTextEditor value={signature.html} onChange={(html) => setSignature(p => ({ ...p, html }))}
                  placeholder={t.signaturePlaceholder} minHeight="150px" />
              </div>
              <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleSaveSignature}>
                {t.save}
              </Button>
            </div>
          </>
        )}

        {/* === TEMPLATES VIEW === */}
        {view === 'templates' && (
          <>
            <div className="flex items-center gap-2 p-3 border-b">
              <Button variant="ghost" size="sm" onClick={() => setView('list')}>
                <ChevronLeft className="h-4 w-4 mr-1" />{t.back}
              </Button>
              <h3 className="font-medium flex-1">{t.templates}</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Create/Edit template */}
              <Card>
                <CardContent className="p-4 space-y-3">
                  <h4 className="font-medium text-sm">{editingTemplate ? 'Editar' : t.createTemplate}</h4>
                  <Input placeholder={t.templateName} value={templateForm.name}
                    onChange={(e) => setTemplateForm(p => ({ ...p, name: e.target.value }))} />
                  <Input placeholder={t.subject} value={templateForm.subject}
                    onChange={(e) => setTemplateForm(p => ({ ...p, subject: e.target.value }))} />
                  <RichTextEditor value={templateForm.body_html}
                    onChange={(html) => setTemplateForm(p => ({ ...p, body_html: html }))}
                    minHeight="120px" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSaveTemplate}>{t.save}</Button>
                    {editingTemplate && <Button size="sm" variant="ghost" onClick={() => { setEditingTemplate(null); setTemplateForm({ name: '', subject: '', body_html: '' }); }}>{t.cancel}</Button>}
                  </div>
                </CardContent>
              </Card>
              {/* List templates */}
              {templates.map(tmpl => (
                <Card key={tmpl.template_id} className="hover:bg-slate-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{tmpl.name}</p>
                        <p className="text-sm text-slate-500">{tmpl.subject}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleUseTemplate(tmpl)}><Send className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => { setEditingTemplate(tmpl.template_id); setTemplateForm({ name: tmpl.name, subject: tmpl.subject, body_html: tmpl.body_html }); }}>
                          <Pen className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500"
                          onClick={async () => { await axios.delete(`${API}/email-tool/templates/${tmpl.template_id}`); loadTemplates(); }}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailToolTab;
